// Enums matching backend definitions
export enum ProofreadingRuleType {
  GRAMMAR = 'grammar',
  LEGAL = 'legal',
  STYLE = 'style',
  CONTENT = 'content',
  FORMATTING = 'formatting',
}

// Interface definitions matching backend models
export interface ProofreadingRule {
  id: string;
  title: string;
  description: string;
  prompt: string;
  rule_type: ProofreadingRuleType;
  enabled: boolean;
  is_global: boolean;
  firm_id: string | null;
  created_at: string;
  modified_at: string;
}

export enum ProofreadingRuleResultStatus {
  NO_ISSUES_FOUND = 'no_issues_found',
  MINOR_ISSUES_FOUND = 'minor_issues_found',
  MODERATE_ISSUES_FOUND = 'moderate_issues_found',
  CRITICAL_ISSUES_FOUND = 'critical_issues_found',
  UNABLE_TO_ASSESS = 'unable_to_assess',
  PROCESSING = 'processing',
  FAILED = 'failed',
}

/**
 * Job level processing status. Mirrors `ProofreadingJobStatus`.
 */
export enum ProofreadingJobStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

/** Finding types attached to a rule result. Mirrors `ProofreadingFindingType`. */
export enum ProofreadingFindingType {
  ISSUE = 'issue',
  EVIDENCE = 'evidence',
}

export interface ProofreadingFinding {
  id: string;
  finding_type: ProofreadingFindingType;
  description: string;
  citation_ids: string[];
  section_name?: string | null;
  human_response?: 'revised' | 'not_applicable' | 'incorrect' | null;
  responded_by?: string | null;
  responded_at?: string | null;
}

export interface ProofreadingRuleResult {
  id: string;
  rule_id: string;
  title: string;
  status: ProofreadingRuleResultStatus;
  reason: string | null;
  findings: ProofreadingFinding[];
}

export interface ProofreadingJob {
  id: string;
  status: ProofreadingJobStatus;
  workspace_id: string;
  version: number;
  created_at: string;
  completed_at: string | null;
  results: ProofreadingRuleResult[];
}

// -----------------------------------------------------------------------------
// Payload sent by the client when creating or updating a firm-specific rule
// -----------------------------------------------------------------------------

export interface CreateProofreadingRule {
  title: string;
  description: string;
  prompt: string;
  rule_type: ProofreadingRuleType;
  /** Whether the rule is enabled (defaults to true if omitted). */
  enabled?: boolean;
  /** Firm identifier (convenient for forms, ignored by backend path scoping). */
  firm: string;
}
